import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon, ChevronDownIcon, FacebookIcon, IdCardIcon, LeafIcon, MapPinIcon, MenuIcon, SearchIcon, SmartphoneIcon, UserRoundIcon, XIcon, YoutubeIcon } from 'lucide-react';
import { Logo } from './Logo';
import { BellPreview } from './avis/BellPreview';
import { HeaderTooltip } from './HeaderTooltip';
import { AnimatePresence } from 'framer-motion';
import { navItems, utilityLinks } from '../data/navigation';

/** The bciti+ modules added to the Town's own header. */
const modules = [{
  label: 'Map',
  to: '/map',
  Icon: MapPinIcon
}, {
  label: 'My Card',
  to: '/my-card',
  Icon: IdCardIcon
}, {
  label: 'My Dashboard',
  to: '/my-dashboard',
  Icon: SmartphoneIcon
}, {
  label: 'My Profile',
  to: '/my-profile',
  Icon: UserRoundIcon
}];
const moduleButton = 'group relative grid h-10 w-10 place-items-center rounded-full text-white transition-colors duration-150 ease-ville hover:bg-white/15 hover:text-ville-spring focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring';
export function Header() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  return <header className="w-full" data-id="element-1268" data-mp="2hnlhexgpggw3">
      <div className="w-full bg-ville-navy" data-id="element-1269" data-mp="2hodeydtv4op0">
        <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-5 py-2 lg:px-10" data-id="element-1270" data-mp="2hpohybfhhsf2">
          <button type="button" aria-label="Search the site" className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/10 p-1 pr-2.5 text-white transition-colors duration-150 ease-ville hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring" data-id="element-1271" data-mp="3fakf9bj4vuvk">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-ville-spring" aria-hidden="true" data-id="element-1272" data-mp="30m65egnav65i" />
            <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-1273" data-mp="1yirs6krdwif" />
          </button>

          <nav aria-label="Utility navigation" className="ml-auto hidden lg:block" data-id="element-1274" data-mp="3t9sjiiu5lt6m">
            <ul className="flex items-center gap-5" data-id="element-1275" data-mp="1rew4e817w9me">
              {utilityLinks.map(label => <li key={label} data-id="element-1276" data-mp="1wcxrd41rkt35">
                  <a href="#accueil" className="text-[15px] text-white transition-colors duration-150 ease-ville hover:text-ville-spring" data-id="element-1277" data-mp="1wbuwkl1bm6hy">
                    {label}
                  </a>
                </li>)}
              <li data-id="element-1278" data-mp="1mkrvvkcaztj7">
                <a href="#accueil" lang="fr" className="text-[15px] text-white transition-colors duration-150 ease-ville hover:text-ville-spring" data-id="element-1279" data-mp="18n3e4ldj92id">
                  Français
                </a>
              </li>
            </ul>
          </nav>

          <Link to="/my-dashboard" className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-full bg-ville-blue px-4 py-1.5 text-[15px] font-bold text-white transition-colors duration-150 ease-ville hover:bg-ville-spring hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring lg:ml-0" data-id="element-1280" data-mp="yq7df1tvlkod">
            <LeafIcon className="h-4 w-4" aria-hidden="true" data-id="element-1281" data-mp="2xzxbtq1g3okr" />
            Online Services
          </Link>
        </div>
      </div>

      <div className="w-full bg-ville-blue" data-id="element-1282" data-mp="hftljywhjizq">
        <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-5 py-2.5 lg:px-10" data-id="element-1283" data-mp="hh4t89t1xaw4">
          <Link to="/" className="flex shrink-0 items-center gap-3 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring" data-id="element-1284" data-mp="w60yjz3q49t1">
            <Logo variant="icon" data-id="element-1285" data-mp="154w3l7v17dur" />
            <span className="font-display text-[19px] font-extrabold text-ville-lime" data-id="element-1286" data-mp="1544g6kbl8mfk">Home</span>
          </Link>

          <nav aria-label="Main navigation" className="hidden lg:block" onMouseLeave={() => setOpenMenu(null)} data-id="element-1287" data-mp="105jgqn45u6s6">
            <ul className="flex items-center" data-id="element-1288" data-mp="1f2k8urq9k3r6">
              {navItems.map(item => {
              const isOpen = openMenu === item.label;
              return <li key={item.label} className="relative" data-id="element-1289" data-mp="3gu6v6fz7nahb">
                    <button type="button" aria-expanded={isOpen} onMouseEnter={() => setOpenMenu(item.label)} onClick={() => setOpenMenu(isOpen ? null : item.label)} className={`flex items-center gap-1 rounded-full px-3 py-2 font-display text-[17px] font-extrabold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring ${isOpen ? 'text-ville-lime' : 'text-white hover:text-ville-lime'}`} data-id="element-1290" data-mp="3gwt3wixu7w14">
                      {item.label}
                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ease-ville ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1291" data-mp="2gxnb6x0ujgc1" />
                    </button>

                    {isOpen && <div className="absolute left-0 top-full w-72 pt-3" data-id="element-1292" data-mp="1p7ikkgmhtx2d">
                        <ul className="rounded-2xl border-2 border-ville-navy bg-white p-2 shadow-xl shadow-ville-navy/25" data-id="element-1293" data-mp="1t82freniaqlv">
                          {item.children.map(child => <li key={child} data-id="element-1294" data-mp="11ho65umj00g0">
                              <a href="#accueil" className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep" data-id="element-1295" data-mp="11gwn1ja47v6m">
                                {child}
                              </a>
                            </li>)}
                        </ul>
                      </div>}
                  </li>;
            })}
            </ul>
          </nav>

          <div className="ml-auto flex items-center gap-1" data-id="element-1296" data-mp="p0wxz3tpv2wo">
            <a href="#accueil" aria-label="Facebook" className="hidden h-9 w-9 place-items-center rounded-full text-white transition-colors duration-150 ease-ville hover:text-ville-spring focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring lg:grid" data-id="element-1297" data-mp="jz7mxo6hup8l">
              <FacebookIcon className="h-4 w-4" aria-hidden="true" data-id="element-1298" data-mp="5al8doooy1if" />
            </a>
            <a href="#accueil" aria-label="YouTube" className="hidden h-9 w-9 place-items-center rounded-full text-white transition-colors duration-150 ease-ville hover:text-ville-spring focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring lg:grid" data-id="element-1299" data-mp="19rjarziigny">
              <YoutubeIcon className="h-4 w-4" aria-hidden="true" data-id="element-1300" data-mp="3nisrszlu6pn8" />
            </a>

            <span className="mx-1.5 hidden h-6 w-px bg-white/25 md:block" aria-hidden="true" data-id="element-1301" data-mp="3driwsz3pcw0l" />

            <div className="hidden items-center gap-1 md:flex" data-id="element-1302" data-mp="3itrozt8r3u1n">
              <Link to={modules[0].to} aria-label="Map" className={moduleButton} data-id="element-1303" data-mp="33vpl1hxt56jv">
                <MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-1304" data-mp="38tr1rw7rgts6" />
                <HeaderTooltip label="Map" data-id="element-1305" data-mp="38w3k4ahfl65r" />
              </Link>
              <Link to={modules[1].to} aria-label="My Card" className={moduleButton} data-id="element-1306" data-mp="fihdbfe79b3">
                <IdCardIcon className="h-5 w-5" aria-hidden="true" data-id="element-1307" data-mp="5dk2s30ajkpq" />
                <HeaderTooltip label="My Card" data-id="element-1308" data-mp="3mts6xnv26ndg" />
              </Link>

              <div className="relative" data-id="element-1309" data-mp="3mtiezuqge654">
                <button type="button" aria-label="My notifications, 3 unread including one urgent alert" aria-expanded={bellOpen} onClick={() => setBellOpen(v => !v)} className={moduleButton} data-id="element-1310" data-mp="3rsd4ffixesy7">
                  <BellIcon className="h-5 w-5" aria-hidden="true" data-id="element-1311" data-mp="o3i9aeofx5r9" />
                  <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-ville-coral px-1 text-[11px] font-bold text-white ring-2 ring-ville-blue" aria-hidden="true" data-id="element-1312" data-mp="o5uyncwj2iec">
                    3
                  </span>
                  {!bellOpen && <HeaderTooltip label="My notifications" data-id="element-1313" data-mp="2ijgyhtczrpqf" />}
                </button>
                <AnimatePresence data-id="element-1314" data-mp="2dm8ctismx3kf">
                  {bellOpen && <BellPreview onNavigate={() => setBellOpen(false)} data-id="element-1315" data-mp="2dn9rdx8v086w" />}
                </AnimatePresence>
              </div>

              <Link to={modules[2].to} aria-label="My Dashboard" className={moduleButton} data-id="element-1316" data-mp="1kwrmqrshlyrd">
                <SmartphoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-1317" data-mp="1yvr9ftwfw3kw" />
                <HeaderTooltip label="My Dashboard" data-id="element-1318" data-mp="1ytod6s4p1e7q" />
              </Link>
              <Link to={modules[3].to} aria-label="My Profile" className={moduleButton} data-id="element-1319" data-mp="1usur84ata9hh">
                <UserRoundIcon className="h-5 w-5" aria-hidden="true" data-id="element-1320" data-mp="3a9v83ddz2fwa" />
                <HeaderTooltip label="My Profile" data-id="element-1321" data-mp="36ack28qutop8" />
              </Link>
            </div>

            <button type="button" aria-label={mobileOpen ? 'Close the menu' : 'Open the menu'} aria-expanded={mobileOpen} onClick={() => setMobileOpen(v => !v)} className="grid h-10 w-10 place-items-center rounded-full text-white transition-colors duration-150 ease-ville hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-spring lg:hidden" data-id="element-1322" data-mp="13nfn3648v07o">
              {mobileOpen ? <XIcon className="h-5 w-5" data-id="element-1323" data-mp="1mdnljax012hj" /> : <MenuIcon className="h-5 w-5" data-id="element-1324" data-mp="1mcdlwhahzm8s" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && <nav aria-label="Mobile navigation" className="bg-ville-deep px-5 pb-6 lg:hidden" data-id="element-1325" data-mp="1rax88it0701o">
          <ul className="grid gap-2 py-4 md:hidden" data-id="element-1326" data-mp="1wc4g0hv5bz8c">
            {modules.map(module => <li key={module.label} data-id="element-1327" data-mp="21axry5usbi6e">
                <Link to={module.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 text-[15px] font-bold text-white" data-id="element-1328" data-mp="219wg3z5y2rnm">
                  <module.Icon className="h-5 w-5" aria-hidden="true" data-id="element-1329" data-mp="6xg74qlrkzf2" />
                  {module.label}
                </Link>
              </li>)}
          </ul>
          <ul className="divide-y divide-white/10" data-id="element-1330" data-mp="2fgg87jdx3hdr">
            {navItems.map(item => <li key={item.label} className="py-3" data-id="element-1331" data-mp="2fed9mayp8fxf">
                <p className="font-display text-sm font-extrabold uppercase tracking-wide text-ville-lime" data-id="element-1332" data-mp="2bfnxsmtb76fw">
                  {item.label}
                </p>
                <ul className="mt-2 space-y-1" data-id="element-1333" data-mp="335skis4jk5ka">
                  {item.children.map(child => <li key={child} data-id="element-1334" data-mp="333odi1bciqfs">
                      <a href="#accueil" className="block py-1.5 text-[15px] text-white/80" data-id="element-1335" data-mp="2z3nstyxnlj0a">
                        {child}
                      </a>
                    </li>)}
                </ul>
              </li>)}
          </ul>
          <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-white/10 pt-4" data-id="element-1336" data-mp="2p57zrqf2fzo5">
            {utilityLinks.map(label => <li key={label} data-id="element-1337" data-mp="1ir8l46xnh07q">
                <a href="#accueil" className="text-sm text-white/80" data-id="element-1338" data-mp="1dr45ej4kg9ik">
                  {label}
                </a>
              </li>)}
            <li data-id="element-1339" data-mp="1d3z2oh958l8x">
              <a href="#accueil" lang="fr" className="text-sm text-white/80" data-id="element-1340" data-mp="1d3fietw0tkrq">
                Français
              </a>
            </li>
          </ul>
        </nav>}
    </header>;
}